import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

interface ShareWithClientModalProps {
  open: boolean;
  onClose: () => void;
  shareUrl: string | null;
  isLoading: boolean;
  error: string | null;
  onGenerateShare?: (displayName: string) => void;
  /** Live previews are named by the backend, so the name input is hidden for them. */
  showNameInput?: boolean;
}

export default function ShareWithClientModal({
  open,
  onClose,
  shareUrl,
  isLoading,
  error,
  onGenerateShare,
  showNameInput = true,
}: ShareWithClientModalProps) {
  const [copied, setCopied] = useState(false);
  const [displayName, setDisplayName] = useState('');

  // handleShare always resolves preview_url to an absolute link (toShareableUrl)
  const fullUrl = shareUrl ?? '';

  const handleCopy = async () => {
    if (fullUrl) {
      try {
        await navigator.clipboard.writeText(fullUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Failed to copy to clipboard
      }
    }
  };

  const handleClose = () => {
    setCopied(false);
    setDisplayName('');
    onClose();
  };

  const handleGenerate = () => {
    if (onGenerateShare) {
      onGenerateShare(displayName);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Share Preview with Client</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Name input - visible until URL is generated, unless the backend names the preview */}
        {!shareUrl && showNameInput && (
          <TextField
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Preview name (optional)"
            size="small"
            disabled={isLoading}
            sx={{ mb: 2 }}
          />
        )}

        {/* Loading state */}
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Generating link...
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success state - show URL */}
        {!isLoading && !error && shareUrl && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Share this link with your client:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                value={fullUrl}
                InputProps={{
                  readOnly: true,
                }}
                size="small"
              />
              <IconButton
                onClick={handleCopy}
                color={copied ? 'success' : 'default'}
                title="Copy to clipboard"
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" sx={{ mt: 1, color: 'success.main', display: 'block' }}>
                Copied!
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!shareUrl ? (
          <>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              variant="contained"
              disabled={isLoading || !onGenerateShare}
            >
              Generate Link
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} variant="outlined">
              Close
            </Button>
            <Button
              onClick={() => {
                window.open(fullUrl, '_blank', 'noopener,noreferrer');
              }}
              variant="contained"
            >
              Open Preview
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
